/**
 * ShiftTypeSelector — kleine dropdown die shift_type upsert naar /api/workforce/schedule.
 *
 * Gedrag:
 *   - Rendered as a badge met huidige shift, of '+' als null.
 *   - onClick → toont <select>, onChange → save.
 *   - Save: PATCH als scheduleId meegegeven is, anders POST (met fallback tijden).
 */

import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  SHIFT_TYPE_VALUES,
  SHIFT_LABELS,
  shiftColor,
  type ShiftTypeValue,
} from '../../lib/shift-labels';

interface Props {
  staffId: string;
  date: string;
  currentShift: ShiftTypeValue | null;
  scheduleId: string | null;
  /** Fallback tijden bij nieuw schedule-record. Default 07:00-16:00. */
  defaultTimeFrom?: string;
  defaultTimeTo?: string;
  onSaved: (result: { shiftType: ShiftTypeValue | null; scheduleId: string | null }) => void;
}

export function ShiftTypeSelector({
  staffId,
  date,
  currentShift,
  scheduleId,
  defaultTimeFrom = '07:00',
  defaultTimeTo = '16:00',
  onSaved,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveShift = async (newShift: ShiftTypeValue | null) => {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Niet ingelogd');

      const headers: Record<string, string> = {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      };

      if (scheduleId) {
        const res = await fetch(`/api/workforce/schedule?id=${scheduleId}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ shift_type: newShift }),
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        onSaved({ shiftType: newShift, scheduleId: data.id });
      } else if (newShift) {
        const res = await fetch('/api/workforce/schedule', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            staff_id: staffId,
            date,
            shift_type: newShift,
            time_from: defaultTimeFrom,
            time_to: defaultTimeTo,
          }),
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        onSaved({ shiftType: newShift, scheduleId: data.id });
      }
      // Else: no schedule + "—" selected → no-op
    } catch (err) {
      console.error('[ShiftTypeSelector] save error:', err);
      alert('Opslaan shift mislukt');
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <select
        autoFocus
        disabled={saving}
        value={currentShift ?? ''}
        onChange={e => {
          const val = e.target.value === '' ? null : (e.target.value as ShiftTypeValue);
          saveShift(val);
        }}
        onBlur={() => !saving && setEditing(false)}
        onClick={e => e.stopPropagation()}
        className="text-xs border rounded px-1 py-0.5"
      >
        <option value="">—</option>
        {SHIFT_TYPE_VALUES.map(v => (
          <option key={v} value={v}>{SHIFT_LABELS[v]}</option>
        ))}
      </select>
    );
  }

  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-medium cursor-pointer ${shiftColor(currentShift)}`}
      onClick={e => { e.stopPropagation(); setEditing(true); }}
      title="Klik om shift te zetten"
    >
      {currentShift ? SHIFT_LABELS[currentShift] : <span className="text-slate-300">+</span>}
    </span>
  );
}
